// Generated by scripts/build-content.mjs from content/docs/*.md and content/pages/*.md
// — do not edit by hand. Run `npm run content` after changing the markdown.

import type { DocsPage } from './docs';
import type { LandingPage } from './pages';

export const GENERATED_DOC_PAGES: DocsPage[] = [
  {
    "path": "/docs/dns",
    "label": "DNS onboarding",
    "title": "Connect a catch-all subdomain",
    "description": "Add MailBots TXT and MX records, verify DNS, complete an external delivery probe and provision a catch-all mailbox.",
    "lead": "Use a dedicated subdomain such as otp.example.com. MailBots never needs to replace the MX records for your root company domain.",
    "updated": "2026-09-17",
    "sections": [
      {
        "id": "before-you-start",
        "title": "Before you start",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Activate a Starter or Pro plan and open Dashboard → Domains.",
              "Choose a subdomain you control, for example otp.example.com. Do not enter example.com itself.",
              "Make sure you can edit DNS at the authoritative DNS provider for the parent domain."
            ]
          },
          {
            "type": "note",
            "text": "Changing the MX record on the root domain can interrupt normal company email. Only configure the subdomain shown by MailBots."
          }
        ]
      },
      {
        "id": "records",
        "title": "Add the two DNS records",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Copy the exact values shown for your domain in the dashboard. The example token above is not valid for your domain."
          },
          {
            "type": "paragraph",
            "text": "If your DNS provider uses relative host names, it may display only _mailbots-challenge.otp and otp. That is normal as long as the final fully-qualified names match."
          },
          {
            "type": "facts",
            "rows": [
              [
                "TXT name",
                "_mailbots-challenge.otp.example.com"
              ],
              [
                "TXT content",
                "mailbots-verification=mb-…"
              ],
              [
                "MX name",
                "otp.example.com"
              ],
              [
                "MX target",
                "mx1.mailbots.top"
              ],
              [
                "MX priority",
                "10"
              ],
              [
                "TTL",
                "Auto or 300 seconds"
              ]
            ]
          },
          {
            "type": "note",
            "text": "For Cloudflare DNS, keep the mail host DNS-only. MX records are never proxied. Remove conflicting MX records on the same subdomain unless you intentionally operate multiple mail exchangers."
          }
        ]
      },
      {
        "id": "verify",
        "title": "Verify TXT and MX",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Wait for DNS propagation, usually a few minutes but sometimes longer depending on the previous TTL.",
              "Select Check DNS. MailBots checks the authoritative result through public DNS.",
              "Continue only when both TXT ownership and MX routing show as verified."
            ]
          },
          {
            "type": "note",
            "text": "If only one check passes, keep the successful record and correct the missing one. Do not create a second domain entry."
          }
        ]
      },
      {
        "id": "probe",
        "title": "Prove real mail delivery",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Select Get probe address after TXT and MX are verified.",
              "Send one normal email from an external provider such as Gmail or Outlook to the generated mailbots-probe address.",
              "Return to the dashboard and select Refresh status. The Probe step becomes complete after the gateway receives the message."
            ]
          },
          {
            "type": "note",
            "text": "Forwarding or sending from the same server is not a useful delivery test. Use a genuinely external mailbox."
          }
        ]
      },
      {
        "id": "provision",
        "title": "Provision and save credentials",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Select Provision only after the Probe step is complete.",
              "Save the generated login and password immediately. The password is shown once and is not stored in recoverable form.",
              "Use any address at your subdomain for incoming mail. Use the generated mailbox login—not the recipient address—to authenticate over IMAPS or POP3S."
            ]
          },
          {
            "type": "note",
            "text": "If the password is lost, rotate the mailbox credential from the dashboard. Rotation invalidates the previous password."
          }
        ]
      }
    ]
  },
  {
    "path": "/docs/mail-clients",
    "label": "POP3S & IMAPS",
    "title": "Read catch-all mail in a mail client",
    "description": "Connect Thunderbird, Outlook or another mail client to MailBots using encrypted IMAPS or POP3S.",
    "lead": "MailBots is receive-only. Use IMAPS for synchronized folders or POP3S when an automation tool needs to download messages.",
    "updated": "2026-09-17",
    "sections": [
      {
        "id": "settings",
        "title": "Connection settings",
        "blocks": [
          {
            "type": "facts",
            "rows": [
              [
                "IMAP server",
                "mx1.mailbots.top"
              ],
              [
                "IMAP port",
                "993"
              ],
              [
                "IMAP security",
                "SSL/TLS (IMAPS)"
              ],
              [
                "POP3 server",
                "mx1.mailbots.top"
              ],
              [
                "POP3 port",
                "995"
              ],
              [
                "POP3 security",
                "SSL/TLS (POP3S)"
              ],
              [
                "Username",
                "The full generated mbx_…@your-subdomain login"
              ],
              [
                "Password",
                "The one-time password shown at provision or rotation"
              ],
              [
                "Authentication",
                "Normal password over TLS"
              ]
            ]
          },
          {
            "type": "note",
            "text": "Ports 143, 110, 465 and 587 are not available. MailBots does not provide outbound SMTP."
          }
        ]
      },
      {
        "id": "addresses",
        "title": "Catch-all addresses and login",
        "blocks": [
          {
            "type": "paragraph",
            "text": "After activation, mail sent to any local part at the connected subdomain is delivered to the same mailbox—for example build-102@otp.example.com and checkout-test@otp.example.com."
          },
          {
            "type": "paragraph",
            "text": "Those recipient addresses are not usernames. Always authenticate with the generated mbx_… login shown in the dashboard."
          }
        ]
      },
      {
        "id": "client-setup",
        "title": "Generic client setup",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Choose manual account configuration and select IMAP or POP3.",
              "Enter the server, encrypted port, full generated username and password exactly as shown above.",
              "Disable outgoing mail or leave SMTP unconfigured. Test receiving with a new external message.",
              "If authentication fails after rotation, remove the saved password from the client and enter the new one."
            ]
          }
        ]
      }
    ]
  },
  {
    "path": "/docs/api",
    "label": "REST API",
    "title": "Automate MailBots with the REST API",
    "description": "Create a scoped MailBots API key and read domains, messages and monthly usage through the versioned REST API.",
    "lead": "The API is read-only and organization-scoped. API keys are shown once and require an active paid subscription.",
    "updated": "2026-09-24",
    "sections": [
      {
        "id": "authentication",
        "title": "Authentication",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Open Dashboard → Developer and create a named API key.",
              "Save the token immediately; only its prefix is displayed later.",
              "Send the token in the Authorization header. Never place it in a URL, client-side bundle or source repository."
            ]
          },
          {
            "type": "facts",
            "rows": [
              [
                "Base URL",
                "https://api.mailbots.top/v1"
              ],
              [
                "Header",
                "Authorization: Bearer mb_live_…"
              ],
              [
                "Available scopes",
                "domains:read, messages:read, usage:read"
              ]
            ]
          }
        ]
      },
      {
        "id": "domains",
        "title": "List domains",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Requires domains:read. The response includes TXT, MX, probe and provisioning timestamps for each connected domain."
          },
          {
            "type": "code",
            "text": "curl -sS https://api.mailbots.top/v1/domains -H 'Authorization: Bearer mb_live_REPLACE_ME'"
          }
        ]
      },
      {
        "id": "messages",
        "title": "List messages",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Requires messages:read. Limit is 1–100. Use the returned next value as the before query parameter to request the next page. Message content expires with the plan retention window."
          },
          {
            "type": "code",
            "text": "curl -sS 'https://api.mailbots.top/v1/messages?limit=50' -H 'Authorization: Bearer mb_live_REPLACE_ME'"
          },
          {
            "type": "paragraph",
            "text": "Messages are returned newest first in `data`. Each one carries these fields:"
          },
          {
            "type": "table",
            "head": [
              "Field",
              "Type",
              "Meaning"
            ],
            "rows": [
              [
                "`id`",
                "string",
                "Message identifier"
              ],
              [
                "`mailbox_id`",
                "string",
                "Mailbox that received it"
              ],
              [
                "`envelope_to`",
                "string",
                "Address it was delivered to, such as build-102@otp.example.com"
              ],
              [
                "`from_addr`",
                "string or null",
                "Sender address"
              ],
              [
                "`subject`",
                "string or null",
                "Subject line"
              ],
              [
                "`otp`",
                "string or null",
                "Verification code detected in the message, or null when none was found"
              ],
              [
                "`normalized_payload`",
                "string",
                "JSON text holding the body as plain text, as `{\"text\": \"…\"}`. Parse it to read magic links"
              ],
              [
                "`raw_size`",
                "number",
                "Size of the original message in bytes"
              ],
              [
                "`received_at_ms`",
                "number",
                "Time received, in Unix milliseconds"
              ],
              [
                "`expires_at`",
                "number",
                "Time it will be deleted, in Unix seconds"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "To wait for one email in a test, poll this endpoint and match on `envelope_to`. The [Playwright](/docs/playwright-email-testing) and [Cypress](/docs/cypress-email-testing) guides show complete examples."
          }
        ]
      },
      {
        "id": "usage",
        "title": "Read monthly usage",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Requires usage:read. Month must use YYYY-MM. The response contains accepted and rejected message counts plus accepted bytes."
          },
          {
            "type": "code",
            "text": "curl -sS 'https://api.mailbots.top/v1/usage?month=2026-09' -H 'Authorization: Bearer mb_live_REPLACE_ME'"
          }
        ]
      },
      {
        "id": "errors",
        "title": "Errors and key hygiene",
        "blocks": [
          {
            "type": "facts",
            "rows": [
              [
                "401",
                "Missing, invalid, expired, revoked or insufficiently scoped API key"
              ],
              [
                "422",
                "Invalid query input such as a malformed month"
              ],
              [
                "429",
                "A service quota or rate limit was reached"
              ]
            ]
          },
          {
            "type": "note",
            "text": "Revoke a key immediately if it is exposed. Create separate keys for separate systems so each can be revoked independently."
          }
        ]
      }
    ]
  },
  {
    "path": "/docs/webhooks",
    "label": "Webhooks",
    "title": "Receive signed message webhooks",
    "description": "Register a MailBots webhook, verify its HMAC-SHA256 signature and handle retries for message.received events.",
    "lead": "Webhooks deliver message metadata and detected OTPs to an HTTPS endpoint. Verify the signature against the exact raw request body before parsing JSON.",
    "updated": "2026-09-17",
    "sections": [
      {
        "id": "create",
        "title": "Create an endpoint",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Expose a public HTTPS URL that accepts POST requests and returns a 2xx response within 10 seconds.",
              "Open Dashboard → Developer, enter the URL and select Add.",
              "Save the mbwh_… signing secret immediately. It is shown once."
            ]
          },
          {
            "type": "note",
            "text": "Redirects are not followed. Use the final HTTPS endpoint URL, not a URL shortener or an HTTP-to-HTTPS redirect."
          }
        ]
      },
      {
        "id": "request",
        "title": "Request format",
        "blocks": [
          {
            "type": "facts",
            "rows": [
              [
                "Method",
                "POST"
              ],
              [
                "Content-Type",
                "application/json"
              ],
              [
                "User-Agent",
                "MailBots-Webhook/1.0"
              ],
              [
                "Event header",
                "x-mailbots-event: message.received"
              ],
              [
                "Timestamp header",
                "x-mailbots-timestamp: Unix seconds"
              ],
              [
                "Signature header",
                "x-mailbots-signature: v1=<hex digest>"
              ]
            ]
          },
          {
            "type": "code",
            "text": "{\n  \"id\": \"msg_…\",\n  \"event\": \"message.received\",\n  \"mailboxId\": \"mbx_…\",\n  \"envelopeTo\": \"build-102@otp.example.com\",\n  \"from\": \"noreply@example.dev\",\n  \"subject\": \"Your verification code\",\n  \"otp\": \"847291\",\n  \"receivedAt\": \"2026-09-16T09:00:00.000Z\",\n  \"expiresAt\": \"2026-09-19T09:00:00.000Z\"\n}"
          }
        ]
      },
      {
        "id": "signature",
        "title": "Verify the signature",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Read the raw request bytes before JSON parsing.",
              "Build the signed value as <timestamp>.<raw-body>.",
              "Compute HMAC-SHA256 with the mbwh_… secret and encode the digest as lowercase hexadecimal.",
              "Compare it to the v1 value using a timing-safe comparison and reject stale timestamps; five minutes is a reasonable replay window."
            ]
          },
          {
            "type": "code",
            "text": "expected = hex(HMAC_SHA256(webhookSecret, timestamp + '.' + rawBody))\nvalid = timingSafeEqual(expected, signatureHeaderWithoutV1)"
          }
        ]
      },
      {
        "id": "delivery",
        "title": "Delivery and retries",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Any 2xx response marks the delivery successful. Timeouts, network errors and non-2xx responses are retried after approximately 1 minute, 5 minutes, 30 minutes, 2 hours and 10 hours before the delivery is marked failed."
          },
          {
            "type": "note",
            "text": "Make handlers idempotent using the payload id. A retry may deliver the same event more than once."
          }
        ]
      }
    ]
  },
  {
    "path": "/docs/playwright-email-testing",
    "label": "Playwright guide",
    "title": "Test email OTP and signup flows with Playwright",
    "description": "A Playwright test that signs up with a unique address on your MailBots catch-all subdomain, polls the REST API for the verification email and enters the OTP.",
    "lead": "Each test invents its own address on your catch-all subdomain, triggers the email, then polls the MailBots API until the message arrives with its code already extracted. Parallel workers never read each other's mail, and there is nothing to clean up afterwards.",
    "updated": "2026-09-24",
    "sections": [
      {
        "id": "prerequisites",
        "title": "Before you start",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "An active Starter or Pro plan with a provisioned catch-all subdomain. See [DNS onboarding](/docs/dns).",
              "An API key with the `messages:read` scope, created in Dashboard → Developer. See [REST API](/docs/api).",
              "Playwright Test (`@playwright/test`) in a Node.js project."
            ]
          },
          {
            "type": "paragraph",
            "text": "Pass both values to the test run as environment variables:"
          },
          {
            "type": "facts",
            "rows": [
              [
                "MAILBOTS_API_KEY",
                "mb_live_…"
              ],
              [
                "MAILBOTS_DOMAIN",
                "otp.example.com"
              ]
            ]
          },
          {
            "type": "note",
            "text": "Keep the API key in your CI secret store or a local .env file that is not committed. It never needs to reach the browser under test, because Playwright calls the API from Node."
          }
        ]
      },
      {
        "id": "helper",
        "title": "Add a MailBots helper",
        "blocks": [
          {
            "type": "paragraph",
            "text": "The helper creates a unique address and waits for its code. It asks for the 50 newest messages and matches on `envelope_to`, the address the email was delivered to, so it keeps working while other tests receive mail at the same time."
          },
          {
            "type": "code",
            "text": "// tests/mailbots.ts\nimport type { APIRequestContext } from '@playwright/test';\n\nconst API = 'https://api.mailbots.top/v1';\n\ntype Message = {\n  id: string;\n  envelope_to: string;\n  subject: string | null;\n  otp: string | null;\n  received_at_ms: number;\n};\n\n/** A new address for every test, so parallel workers never share an inbox. */\nexport function testAddress(prefix: string): string {\n  const unique = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;\n  return `${prefix}-${unique}@${process.env.MAILBOTS_DOMAIN}`.toLowerCase();\n}\n\n/** Polls MailBots until an email for `address` arrives with a detected code. */\nexport async function waitForOtp(request: APIRequestContext, address: string, timeoutMs = 60_000): Promise<string> {\n  const deadline = Date.now() + timeoutMs;\n  while (Date.now() < deadline) {\n    const response = await request.get(`${API}/messages`, {\n      params: { limit: 50 },\n      headers: { Authorization: `Bearer ${process.env.MAILBOTS_API_KEY}` },\n    });\n    // A bad key or an inactive plan will not fix itself, so fail at once.\n    if (!response.ok()) throw new Error(`MailBots API ${response.status()}: ${await response.text()}`);\n    const { data } = (await response.json()) as { data: Message[] };\n    const message = data.find((item) => item.envelope_to.toLowerCase() === address && item.otp);\n    if (message?.otp) return message.otp;\n    await new Promise((resolve) => setTimeout(resolve, 2_000));\n  }\n  throw new Error(`No email with a code reached ${address} within ${timeoutMs / 1000}s`);\n}",
            "lang": "ts"
          }
        ]
      },
      {
        "id": "test",
        "title": "Write the test",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Replace the URL, labels and button names with the ones in your app. The test timeout is raised because Playwright's default of 30 seconds is shorter than the time the helper is allowed to wait for mail."
          },
          {
            "type": "code",
            "text": "// tests/signup.spec.ts\nimport { test, expect } from '@playwright/test';\nimport { testAddress, waitForOtp } from './mailbots';\n\ntest('a new user verifies their email with a one-time code', async ({ page, request }) => {\n  test.setTimeout(90_000);\n  const address = testAddress('signup');\n\n  await page.goto('/signup');\n  await page.getByLabel('Email').fill(address);\n  await page.getByRole('button', { name: 'Continue' }).click();\n\n  const otp = await waitForOtp(request, address);\n  await page.getByLabel('Verification code').fill(otp);\n  await page.getByRole('button', { name: 'Verify' }).click();\n\n  await expect(page.getByRole('heading', { name: 'Welcome' })).toBeVisible();\n});",
            "lang": "ts"
          },
          {
            "type": "paragraph",
            "text": "Run it locally:"
          },
          {
            "type": "code",
            "text": "MAILBOTS_API_KEY=mb_live_REPLACE_ME MAILBOTS_DOMAIN=otp.example.com npx playwright test",
            "lang": "bash"
          }
        ]
      },
      {
        "id": "ci",
        "title": "Run it in CI",
        "blocks": [
          {
            "type": "paragraph",
            "text": "In GitHub Actions, store the key as a repository secret and pass both variables to the test step:"
          },
          {
            "type": "code",
            "text": "- name: End-to-end tests\n  run: npx playwright test\n  env:\n    MAILBOTS_API_KEY: ${{ secrets.MAILBOTS_API_KEY }}\n    MAILBOTS_DOMAIN: otp.example.com",
            "lang": "yaml"
          },
          {
            "type": "paragraph",
            "text": "There is no cleanup step. Every run uses new addresses, and messages are deleted automatically after 72 hours."
          }
        ]
      },
      {
        "id": "troubleshooting",
        "title": "Troubleshooting",
        "blocks": [
          {
            "type": "table",
            "head": [
              "Symptom",
              "What to check"
            ],
            "rows": [
              [
                "401 from the API",
                "The key is revoked, lacks `messages:read`, or the plan is no longer active"
              ],
              [
                "Timeout, nothing in the dashboard",
                "The app did not send, or DNS for the subdomain changed. Re-run the probe in Dashboard → Domains"
              ],
              [
                "Timeout, message is in the dashboard",
                "More than 50 messages arrived during the wait. Raise `limit` to 100"
              ],
              [
                "Message found but `otp` is null",
                "No convincing code was detected. Read the message in the dashboard and check the email's wording"
              ]
            ]
          },
          {
            "type": "note",
            "text": "Assert on your application, not on MailBots. If a test needs the full message, the same API response includes the sender, subject and received time."
          }
        ]
      }
    ]
  },
  {
    "path": "/docs/cypress-email-testing",
    "label": "Cypress guide",
    "title": "Test verification emails and OTPs with Cypress",
    "description": "A Cypress test that signs up with a unique MailBots catch-all address, reads the verification code through a Node task and keeps the API key out of the browser.",
    "lead": "Cypress runs tests in the browser, so the MailBots API is called from a Node task instead. The key stays in Node, each test gets its own address on your catch-all subdomain, and the task returns the code once the verification email has arrived.",
    "updated": "2026-09-26",
    "sections": [
      {
        "id": "prerequisites",
        "title": "Before you start",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "An active Starter or Pro plan with a provisioned catch-all subdomain. See [DNS onboarding](/docs/dns).",
              "An API key with the `messages:read` scope, created in Dashboard → Developer. See [REST API](/docs/api).",
              "Cypress 10 or later, running on Node.js 18 or later for the built-in fetch. The example was run on Cypress 16.1."
            ]
          },
          {
            "type": "paragraph",
            "text": "Both values are read by the Node task only, so neither reaches the browser under test:"
          },
          {
            "type": "facts",
            "rows": [
              [
                "MAILBOTS_API_KEY",
                "mb_live_…"
              ],
              [
                "MAILBOTS_DOMAIN",
                "otp.example.com"
              ]
            ]
          },
          {
            "type": "note",
            "text": "Do not give either variable the CYPRESS_ prefix. Nothing here uses Cypress.env(), which Cypress 16 removed, so the same code runs from Cypress 10 to 16."
          }
        ]
      },
      {
        "id": "task",
        "title": "Add Node tasks",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Two tasks run in Node. One creates a unique address, the other asks for the 50 newest messages and returns the code sent to that address, or null while it has not arrived. A Cypress task may not resolve to undefined, which is why \"not yet\" is null."
          },
          {
            "type": "code",
            "text": "// cypress.config.js\nconst { defineConfig } = require('cypress');\n\nmodule.exports = defineConfig({\n  e2e: {\n    baseUrl: 'http://localhost:3000',\n    setupNodeEvents(on) {\n      on('task', {\n        mailbotsAddress(prefix) {\n          if (!process.env.MAILBOTS_DOMAIN) throw new Error('MAILBOTS_DOMAIN is not set');\n          const unique = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;\n          return `${prefix}-${unique}@${process.env.MAILBOTS_DOMAIN}`.toLowerCase();\n        },\n        async mailbotsOtp(address) {\n          const response = await fetch('https://api.mailbots.top/v1/messages?limit=50', {\n            headers: { Authorization: `Bearer ${process.env.MAILBOTS_API_KEY}` },\n          });\n          if (!response.ok) throw new Error(`MailBots API ${response.status}: ${await response.text()}`);\n          const { data } = await response.json();\n          const message = data.find((item) => item.envelope_to.toLowerCase() === address && item.otp);\n          return message ? message.otp : null;\n        },\n      });\n    },\n  },\n});",
            "lang": "js"
          }
        ]
      },
      {
        "id": "helper",
        "title": "Add a polling helper",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cypress commands are queued, so the helper retries by chaining another task after a short wait instead of looping."
          },
          {
            "type": "code",
            "text": "// cypress/support/mailbots.js\nexport function waitForOtp(address, attemptsLeft = 30) {\n  return cy.task('mailbotsOtp', address, { log: false }).then((otp) => {\n    if (otp) return otp;\n    if (attemptsLeft <= 1) throw new Error(`No email with a code reached ${address}`);\n    return cy.wait(2000, { log: false }).then(() => waitForOtp(address, attemptsLeft - 1));\n  });\n}",
            "lang": "js"
          }
        ]
      },
      {
        "id": "test",
        "title": "Write the test",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Replace the URL and selectors with the ones in your app."
          },
          {
            "type": "code",
            "text": "// cypress/e2e/signup.cy.js\nimport { waitForOtp } from '../support/mailbots';\n\ndescribe('signup', () => {\n  it('verifies a new account with an email code', () => {\n    cy.task('mailbotsAddress', 'signup').then((address) => {\n      cy.visit('/signup');\n      cy.get('input[type=email]').type(address);\n      cy.contains('button', 'Continue').click();\n\n      waitForOtp(address).then((otp) => {\n        cy.get('input[name=code]').type(otp);\n      });\n    });\n    cy.contains('button', 'Verify').click();\n\n    cy.contains('h1', 'Welcome').should('be.visible');\n  });\n});",
            "lang": "js"
          },
          {
            "type": "paragraph",
            "text": "Run it:"
          },
          {
            "type": "code",
            "text": "MAILBOTS_API_KEY=mb_live_REPLACE_ME MAILBOTS_DOMAIN=otp.example.com npx cypress run",
            "lang": "bash"
          }
        ]
      },
      {
        "id": "ci",
        "title": "Run it in CI",
        "blocks": [
          {
            "type": "code",
            "text": "- name: Cypress\n  run: npx cypress run\n  env:\n    MAILBOTS_API_KEY: ${{ secrets.MAILBOTS_API_KEY }}\n    MAILBOTS_DOMAIN: otp.example.com",
            "lang": "yaml"
          },
          {
            "type": "paragraph",
            "text": "With 30 attempts two seconds apart, a test waits about a minute for mail before failing. Messages expire after 72 hours, so no cleanup step is needed."
          }
        ]
      },
      {
        "id": "troubleshooting",
        "title": "Troubleshooting",
        "blocks": [
          {
            "type": "table",
            "head": [
              "Symptom",
              "What to check"
            ],
            "rows": [
              [
                "Task fails with 401",
                "The key is revoked, lacks `messages:read`, or the plan is no longer active"
              ],
              [
                "Task fails with \"MAILBOTS_DOMAIN is not set\"",
                "The variable is missing from this run, or it was given the CYPRESS_ prefix"
              ],
              [
                "\"Cypress.env() was removed\"",
                "Code copied from an older version of this guide. Create the address with the mailbotsAddress task instead"
              ],
              [
                "Times out, message is in the dashboard",
                "More than 50 messages arrived during the wait. Raise `limit` to 100"
              ],
              [
                "Message found but no code",
                "No convincing code was detected. Read the message in the dashboard"
              ]
            ]
          }
        ]
      }
    ]
  }
];

export const GENERATED_LANDING_PAGES: LandingPage[] = [
  {
    "path": "/browser-extensions",
    "label": "Browser extensions",
    "title": "Disposable Email Extension for Chrome, Edge and Firefox — MailBots",
    "heading": "Disposable email in your browser, with the code already found",
    "description": "Free MailBots extension for Chrome, Edge and Firefox. Create a disposable address, receive the verification email and copy the OTP without leaving the tab.",
    "lead": "MailBots is a free browser extension for Chrome, Microsoft Edge and Firefox that gives you a disposable @mailbots.top email address in one click. Paste the address into a signup form, and the verification email arrives in the extension with the one-time code already detected and ready to copy. No account is needed, and addresses expire after 24 hours unless you extend them.",
    "cta": "stores",
    "updated": "2026-09-24",
    "related": [
      "/firefox-extension",
      "/email-otp-testing",
      "/temporary-email-for-testing",
      "/privacy"
    ],
    "sections": [
      {
        "id": "choose",
        "title": "Choose your browser",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Each store below publishes the same extension after its own review. Install from the store that matches your browser, so updates arrive automatically."
          },
          {
            "type": "stores"
          },
          {
            "type": "paragraph",
            "text": "Brave, Vivaldi, Opera and other Chromium-based browsers install from the Chrome Web Store."
          }
        ]
      },
      {
        "id": "features",
        "title": "What the extension does",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Creates a disposable address on `mailbots.top` in one click. No account, name or email of your own is required.",
              "Shows incoming mail in the popup and highlights the verification code, so you copy the OTP instead of reading the whole message.",
              "Sends a browser notification when new mail arrives.",
              "Keeps up to two addresses active at once. Each lasts 24 hours and can be extended.",
              "Only receives mail. MailBots cannot send email, so an address cannot be used to write to anyone."
            ]
          }
        ]
      },
      {
        "id": "differences",
        "title": "How Chrome, Edge and Firefox differ",
        "blocks": [
          {
            "type": "paragraph",
            "text": "The inbox, code detection and limits are identical in every browser. What differs is how each browser lets an extension wake up when mail arrives while the popup is closed."
          },
          {
            "type": "table",
            "head": [
              "Behavior",
              "Chrome and Edge",
              "Firefox"
            ],
            "rows": [
              [
                "New mail while closed",
                "Web Push notification from the server",
                "Checks for new mail once a minute"
              ],
              [
                "New mail while open",
                "Checks every few seconds",
                "Checks every few seconds"
              ],
              [
                "Pinned view",
                "Side panel",
                "Sidebar"
              ],
              [
                "Permissions",
                "`storage`, `notifications`, `sidePanel`",
                "`storage`, `notifications`, `alarms`"
              ],
              [
                "Network access",
                "`api.mailbots.top` only",
                "`api.mailbots.top` only"
              ],
              [
                "Mobile",
                "Desktop only",
                "Firefox for Android 142 and later"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "The Edge listing is built from the same source as the Chrome one. The [Firefox add-on page](/firefox-extension) explains why Firefox checks on a timer instead."
          }
        ]
      },
      {
        "id": "privacy",
        "title": "Permissions and privacy",
        "blocks": [
          {
            "type": "paragraph",
            "text": "The extension has no content scripts, so it cannot read or change the pages you visit. It talks to a single host, `api.mailbots.top`, and contains no analytics, advertising or remotely loaded code."
          },
          {
            "type": "bullets",
            "items": [
              "You are identified only by an anonymous device token stored in the extension. There is no account.",
              "Message text and detected codes are kept for no more than 24 hours, then deleted.",
              "Delete my data, in the extension menu, removes your addresses, messages, push subscriptions and device token from the server."
            ]
          },
          {
            "type": "paragraph",
            "text": "The [privacy policy](/privacy) describes the full data lifecycle, and the [source code is on GitHub](https://github.com/linhfishCR7/mailbots)."
          }
        ]
      },
      {
        "id": "limits",
        "title": "Free limits",
        "blocks": [
          {
            "type": "facts",
            "rows": [
              [
                "Active addresses",
                "2 at a time"
              ],
              [
                "Address lifetime",
                "24 hours, extendable"
              ],
              [
                "New addresses",
                "5 per hour, 20 per day"
              ],
              [
                "Largest message",
                "1 MiB"
              ],
              [
                "Message retention",
                "24 hours"
              ],
              [
                "Sending",
                "Not supported, receive-only"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "Need addresses on your own domain, API access or a shared team inbox? See [catch-all email testing](/catch-all-email-testing) and [pricing](/pricing)."
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "Is the MailBots extension free?"
          },
          {
            "type": "paragraph",
            "text": "Yes. The extension and its @mailbots.top addresses are free, with no card and no account. Paid plans only add private catch-all subdomains for teams."
          },
          {
            "type": "subheading",
            "text": "Why do some sites reject a disposable address?"
          },
          {
            "type": "paragraph",
            "text": "Some services block known temporary email domains. When a flow must accept an address on your own domain, use a [catch-all subdomain](/catch-all-email-testing) instead."
          },
          {
            "type": "subheading",
            "text": "Can I use it on my phone?"
          },
          {
            "type": "paragraph",
            "text": "On Android, install the add-on in Firefox 142 or later. The Chrome and Edge builds are for desktop browsers."
          },
          {
            "type": "subheading",
            "text": "Is the same inbox available in Telegram?"
          },
          {
            "type": "paragraph",
            "text": "MailBots also runs as a [Telegram bot](https://t.me/mailbotstop_bot) with the same free limits. Addresses created in the bot and in the extension are kept separately."
          }
        ]
      }
    ]
  },
  {
    "path": "/firefox-extension",
    "label": "Firefox add-on",
    "title": "MailBots for Firefox — Disposable Email Add-on with OTP Detection",
    "heading": "A disposable inbox built for Firefox",
    "description": "Free MailBots add-on for Firefox on desktop and Android. Disposable email addresses, automatic OTP detection, a sidebar view and no account required.",
    "lead": "MailBots for Firefox is a free add-on that creates disposable @mailbots.top email addresses and picks the one-time code out of each verification email. It runs in Firefox 140 or later on desktop and Firefox 142 or later on Android, opens from the toolbar or the sidebar, and needs no account. Addresses and the messages they receive expire after 24 hours.",
    "cta": "firefox",
    "updated": "2026-09-24",
    "related": [
      "/browser-extensions",
      "/email-otp-testing",
      "/temporary-email-for-testing",
      "/privacy"
    ],
    "sections": [
      {
        "id": "install",
        "title": "Install from Firefox Add-ons",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Open the MailBots listing on Firefox Add-ons and select Add to Firefox.",
              "Review the permissions Firefox shows, then confirm.",
              "Accept the privacy notice on the welcome page that opens. The add-on does nothing until you do.",
              "Pin MailBots to the toolbar from the Extensions menu, so the inbox is one click away."
            ]
          },
          {
            "type": "paragraph",
            "text": "On Firefox for Android, open the same listing in Firefox, install it, then open MailBots from the Firefox menu under Extensions."
          }
        ]
      },
      {
        "id": "workflow",
        "title": "Use it in a signup flow",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Open MailBots and copy the address it shows.",
              "Paste it into the email field of the signup, login or password-reset form you are testing.",
              "Wait for the message in the popup or the sidebar. The detected code is highlighted with a Copy button.",
              "Paste the code and finish the flow. Select New address when you want a clean inbox for the next run."
            ]
          }
        ]
      },
      {
        "id": "polling",
        "title": "How Firefox checks for new mail",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Chrome and Edge can wake an extension with Web Push. Web Push is not available to Firefox add-ons, so the Firefox build checks for mail itself."
          },
          {
            "type": "bullets",
            "items": [
              "While the popup or sidebar is open, it checks every few seconds, so a code appears soon after the message is delivered.",
              "While it is closed, it checks once a minute and shows a notification when something new has arrived."
            ]
          },
          {
            "type": "paragraph",
            "text": "This is why the Firefox add-on asks for the `alarms` permission, which Chrome and Edge do not need. With the add-on closed, expect a notification up to about a minute after the email is delivered. Open the popup when you are waiting for a code and it shows up sooner."
          }
        ]
      },
      {
        "id": "sidebar",
        "title": "Keep the inbox in the sidebar",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Firefox has no side panel. It has a sidebar, and MailBots registers its inbox as a sidebar panel. Open it from the Firefox sidebar, where MailBots appears alongside bookmarks and history, and keep it next to the form you are testing while you work."
          }
        ]
      },
      {
        "id": "permissions",
        "title": "Permissions and data collection",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "`storage` keeps the anonymous device token, your settings and a record of which messages have already triggered a notification.",
              "`notifications` tells you when new mail arrives.",
              "`alarms` wakes the add-on once a minute to check for mail while it is closed.",
              "Access to `api.mailbots.top` is its only network access. There are no content scripts, so it cannot read or change the pages you visit."
            ]
          },
          {
            "type": "paragraph",
            "text": "Firefox Add-ons also shows a data collection notice. MailBots declares two categories there: personal communications, because it displays the email you receive, and authentication information, because verification codes are exactly that. Both stay on MailBots servers for no more than 24 hours, and Delete my data in the add-on removes them immediately. See the [privacy policy](/privacy) for details."
          }
        ]
      },
      {
        "id": "limits",
        "title": "Limits",
        "blocks": [
          {
            "type": "facts",
            "rows": [
              [
                "Active addresses",
                "2 at a time"
              ],
              [
                "Address lifetime",
                "24 hours, extendable"
              ],
              [
                "New addresses",
                "5 per hour, 20 per day"
              ],
              [
                "Largest message",
                "1 MiB"
              ],
              [
                "Message retention",
                "24 hours"
              ],
              [
                "Firefox version",
                "140+ desktop, 142+ Android"
              ]
            ]
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "Is the Firefox add-on different from the Chrome extension?"
          },
          {
            "type": "paragraph",
            "text": "It is built from the same source. The inbox, code detection and limits are the same. Only the way it checks for mail while closed and the sidebar differ. See [Chrome, Edge and Firefox compared](/browser-extensions#differences)."
          },
          {
            "type": "subheading",
            "text": "Why does a notification sometimes take up to a minute?"
          },
          {
            "type": "paragraph",
            "text": "While closed, the add-on checks once a minute because Firefox cannot wake it with a push message. With the popup or sidebar open, it checks every few seconds."
          },
          {
            "type": "subheading",
            "text": "Can I check how the add-on is built?"
          },
          {
            "type": "paragraph",
            "text": "Yes. The [source is on GitHub](https://github.com/linhfishCR7/mailbots), including the build instructions that reproduce the package submitted to Firefox Add-ons."
          },
          {
            "type": "subheading",
            "text": "Does it cost anything?"
          },
          {
            "type": "paragraph",
            "text": "No. The add-on is free with no account. Teams that need addresses on their own domain can add a [catch-all subdomain](/catch-all-email-testing)."
          }
        ]
      }
    ]
  },
  {
    "path": "/pricing",
    "label": "Pricing",
    "title": "MailBots Pricing — Free, Starter $10 and Pro $15 per Month",
    "heading": "Simple pricing for disposable and catch-all test email",
    "description": "Compare MailBots Free, Starter ($10/month) and Pro ($15/month): addresses, catch-all subdomains, message quotas, storage, retention, API and webhooks.",
    "lead": "MailBots Free gives anyone disposable @mailbots.top addresses in the browser extension or Telegram, at no cost and with no account. Starter at $10 a month and Pro at $15 a month add private catch-all subdomains on your own domain, with a web inbox, POP3S, IMAPS, a REST API and signed webhooks. Plans are monthly, and Paddle handles card and PayPal payments.",
    "cta": "plans",
    "updated": "2026-09-24",
    "related": [
      "/catch-all-email-testing",
      "/browser-extensions",
      "/docs/api",
      "/refund-policy"
    ],
    "sections": [
      {
        "id": "compare",
        "title": "Compare plans",
        "blocks": [
          {
            "type": "table",
            "head": [
              "Feature",
              "Free",
              "Starter",
              "Pro"
            ],
            "rows": [
              [
                "Price",
                "$0",
                "$10 / month",
                "$15 / month"
              ],
              [
                "Addresses",
                "2 active on mailbots.top",
                "Any address on 1 subdomain",
                "Any address on 5 subdomains"
              ],
              [
                "Catch-all subdomains",
                "None",
                "1",
                "5"
              ],
              [
                "Messages",
                "1 MiB each",
                "5,000 / month, 5 MB each",
                "25,000 / month, 5 MB each"
              ],
              [
                "Storage",
                "Not applicable",
                "250 MB",
                "1 GB"
              ],
              [
                "Retention",
                "24 hours",
                "72 hours",
                "72 hours"
              ],
              [
                "Team members",
                "Not applicable",
                "1",
                "3"
              ],
              [
                "Where you read mail",
                "Extension and Telegram",
                "Web inbox, POP3S, IMAPS",
                "Web inbox, POP3S, IMAPS"
              ],
              [
                "REST API and webhooks",
                "No",
                "Yes",
                "Yes"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "Every plan is receive-only. MailBots never sends email on your behalf."
          }
        ]
      },
      {
        "id": "catch-all",
        "title": "What the paid plans add",
        "blocks": [
          {
            "type": "paragraph",
            "text": "With a catch-all subdomain such as `otp.example.com`, every address under it is accepted: `signup-1042@otp.example.com`, `reset-test@otp.example.com` and anything else your test suite invents. There is nothing to create per address. Your company email is unaffected, because only the subdomain points at MailBots."
          },
          {
            "type": "paragraph",
            "text": "Read [how catch-all email testing works](/catch-all-email-testing) or go straight to the [DNS setup guide](/docs/dns)."
          }
        ]
      },
      {
        "id": "quotas",
        "title": "Quotas and what happens at a limit",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Messages count when MailBots accepts them. Once the monthly quota is used, new mail is rejected until the next calendar month and appears as rejected in your usage.",
              "Storage is a rolling total. Mail is deleted after 72 hours, which frees space again. A message that would exceed your storage is rejected.",
              "Messages larger than 5 MB are rejected.",
              "The REST API and webhooks are included in both paid plans, with no charge per request."
            ]
          }
        ]
      },
      {
        "id": "billing",
        "title": "Payment and billing",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Card and PayPal payments are processed by Paddle, the merchant of record. Paddle calculates any sales tax or VAT at checkout and issues the invoice.",
              "Paddle subscriptions renew monthly until you cancel in the customer portal. Canceling stops the next renewal; the plan stays active until the end of the period you paid for.",
              "You can also pay in USDT on the TRC20 network. Each approved payment adds 30 days, is reviewed manually and does not renew automatically.",
              "Refunds follow the [refund policy](/refund-policy)."
            ]
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "Do I need a card to use MailBots for free?"
          },
          {
            "type": "paragraph",
            "text": "No. Install the [browser extension](/browser-extensions) or open the Telegram bot. Free needs no card and no account."
          },
          {
            "type": "subheading",
            "text": "Which plan should a small team choose?"
          },
          {
            "type": "paragraph",
            "text": "Starter fits one person testing one product. Pro fits a team: it allows three members, five subdomains for separate environments such as staging and preview, and five times the monthly messages."
          },
          {
            "type": "subheading",
            "text": "Can MailBots send email?"
          },
          {
            "type": "paragraph",
            "text": "No. All plans are receive-only, and SMTP submission ports 465 and 587 are not exposed."
          },
          {
            "type": "subheading",
            "text": "Is my data used for anything else?"
          },
          {
            "type": "paragraph",
            "text": "No. Mail is deleted when its retention period ends, and MailBots does not sell data or show ads. See the [privacy policy](/privacy)."
          }
        ]
      }
    ]
  },
  {
    "path": "/email-otp-testing",
    "label": "Email OTP testing",
    "title": "Email OTP Testing — Receive and Check Verification Codes | MailBots",
    "heading": "Test email OTP flows without a shared inbox",
    "description": "Test email one-time passwords by hand in your browser or automatically through an API. MailBots receives the verification email and extracts the code for you.",
    "lead": "Email OTP testing checks that your app sends a one-time code, that the code arrives, and that it is accepted once and only once. MailBots handles the inbox side. A free browser extension gives you a fresh address and highlights the code for manual testing, and paid catch-all subdomains return the same code through a REST API or webhook for automated tests.",
    "cta": "stores",
    "updated": "2026-09-24",
    "related": [
      "/temporary-email-for-testing",
      "/catch-all-email-testing",
      "/docs/playwright-email-testing",
      "/docs/cypress-email-testing"
    ],
    "sections": [
      {
        "id": "checklist",
        "title": "What to test in an email OTP flow",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "The code arrives at a brand-new address, not only at addresses your team has used before.",
              "The code in the email matches what your backend issued, with the length and characters you expect.",
              "An expired code is rejected, and the message tells the user what to do next.",
              "A code that has been used cannot be used again.",
              "Requesting a new code invalidates the previous one, if that is the behavior you intend.",
              "Resending is rate-limited, and the limit is explained to the user.",
              "Repeated wrong attempts slow down or lock verification.",
              "The email is readable without images, and the code copies without stray spaces.",
              "Links in the same email point at the environment under test, not at production."
            ]
          }
        ]
      },
      {
        "id": "manual",
        "title": "Manual testing in the browser",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Install the [MailBots extension](/browser-extensions) for Chrome, Edge or Firefox.",
              "Copy the disposable address from the popup and paste it into the form under test.",
              "When the email arrives, MailBots shows the detected code with a Copy button. Paste it into the form.",
              "Select New address to repeat the test from a clean state."
            ]
          },
          {
            "type": "paragraph",
            "text": "Because every address is new, you test the path a real first-time user takes, not an inbox full of earlier codes."
          }
        ]
      },
      {
        "id": "automated",
        "title": "Automated testing through the API",
        "blocks": [
          {
            "type": "paragraph",
            "text": "For CI, give your tests a catch-all subdomain such as `otp.example.com`. Each test invents its own address, triggers the email, then asks the MailBots API for the newest message sent to that address. The response already contains the extracted code in the `otp` field."
          },
          {
            "type": "code",
            "text": "curl -sS 'https://api.mailbots.top/v1/messages?limit=50' \\\n  -H 'Authorization: Bearer mb_live_REPLACE_ME'",
            "lang": "bash"
          },
          {
            "type": "paragraph",
            "text": "Match the message on `envelope_to` and read `otp`. The [Playwright guide](/docs/playwright-email-testing) and the [Cypress guide](/docs/cypress-email-testing) show complete tests. If you would rather be notified than poll, a [signed webhook](/docs/webhooks) posts each message, code included, to your endpoint."
          }
        ]
      },
      {
        "id": "detection",
        "title": "How MailBots finds the code",
        "blocks": [
          {
            "type": "paragraph",
            "text": "MailBots reads the subject and the text of each message and scores every candidate that looks like a code:"
          },
          {
            "type": "bullets",
            "items": [
              "plain numeric codes of 4 to 8 digits, such as `847291`;",
              "grouped codes such as `847 291` or `847-291`;",
              "provider formats such as Google's `G-847291`;",
              "letter-and-digit codes of 6 to 8 characters, such as `A7K2Q9`."
            ]
          },
          {
            "type": "paragraph",
            "text": "Candidates score higher when words such as code, verification, OTP, one-time, sign in or 2FA appear just before them, or when they sit in the subject or near the top of the message. They score lower when they look like a phone number, a year, a time or a price. When no candidate is convincing, MailBots shows no code rather than a wrong one, and the full message is still there to read."
          }
        ]
      },
      {
        "id": "limits",
        "title": "Limits worth knowing",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Free addresses use the shared `mailbots.top` domain, which some services block as disposable. For flows that must accept your own domain, use a [catch-all subdomain](/catch-all-email-testing).",
              "Free messages are kept for 24 hours; catch-all messages for 72 hours.",
              "MailBots only receives. It cannot send the OTP email for you or reply to it.",
              "How quickly a code arrives depends mostly on the service sending it. MailBots shows the message as soon as it is received."
            ]
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "Can I test OTP emails without writing code?"
          },
          {
            "type": "paragraph",
            "text": "Yes. The free [browser extension](/browser-extensions) covers manual testing: copy an address, trigger the email and copy the highlighted code."
          },
          {
            "type": "subheading",
            "text": "Does MailBots work with magic links as well as codes?"
          },
          {
            "type": "paragraph",
            "text": "Yes. The whole message is available, so you can open a magic link from it. Code detection is a convenience on top of the full message."
          },
          {
            "type": "subheading",
            "text": "Can parallel tests share one inbox?"
          },
          {
            "type": "paragraph",
            "text": "With a catch-all subdomain, each test uses its own address, so parallel tests never read each other's codes even though the mail lands in the same mailbox."
          }
        ]
      }
    ]
  },
  {
    "path": "/temporary-email-for-testing",
    "label": "Temporary email for testing",
    "title": "Temporary Email for Testing Signup and Verification | MailBots",
    "heading": "A temporary inbox built for testing",
    "description": "Use temporary email addresses to test signup, verification and password-reset flows. Free disposable inboxes for manual QA, private catch-all subdomains for teams.",
    "lead": "Temporary email for testing is a throwaway inbox for checking signup, verification and password-reset flows without filling a real mailbox with test accounts. MailBots offers two kinds. Free disposable addresses on mailbots.top cover quick manual checks, and private catch-all subdomains on your own domain cover teams and automated tests that need any address to work.",
    "cta": "stores",
    "updated": "2026-09-24",
    "related": [
      "/email-otp-testing",
      "/catch-all-email-testing",
      "/browser-extensions",
      "/acceptable-use"
    ],
    "sections": [
      {
        "id": "use-cases",
        "title": "When a temporary address is the right tool",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Signing up as a brand-new user, again and again, without creating real mailboxes.",
              "Checking email verification codes and magic links.",
              "Walking through password reset and account recovery.",
              "Reviewing welcome, onboarding and other transactional email as a user sees it.",
              "Accepting invitations to a workspace or team from a second account.",
              "Testing a staging or preview environment that should never email a real person."
            ]
          }
        ]
      },
      {
        "id": "why",
        "title": "Why not use your own inbox",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Your own inbox works for one or two checks, then gets in the way. Every test account lands in a mailbox you also use for real mail, and plus-addresses such as you+test1@gmail.com are rejected or stripped by some sign-up forms. A shared team inbox is worse for testing: codes from different testers arrive side by side, so a test can easily read someone else's code."
          },
          {
            "type": "paragraph",
            "text": "A fresh address for each test avoids both problems. Nothing from an earlier run is in the inbox, and nobody's real mail is involved."
          }
        ]
      },
      {
        "id": "compare",
        "title": "Free disposable or private catch-all",
        "blocks": [
          {
            "type": "table",
            "head": [
              "Question",
              "Free disposable",
              "Private catch-all"
            ],
            "rows": [
              [
                "Address",
                "Generated for you on mailbots.top",
                "Anything you like on your own subdomain"
              ],
              [
                "Setup",
                "Install the extension or open the Telegram bot",
                "Add a TXT and an MX record for a subdomain"
              ],
              [
                "Reading mail",
                "Extension popup, sidebar or Telegram",
                "Web inbox, POP3S, IMAPS, REST API, webhooks"
              ],
              [
                "Retention",
                "24 hours",
                "72 hours"
              ],
              [
                "Blocked as disposable",
                "Sometimes",
                "Unlikely, it is your own domain"
              ],
              [
                "Cost",
                "Free",
                "From $10 / month"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "Start with the [free extension](/browser-extensions) for manual checks. Move to a [catch-all subdomain](/catch-all-email-testing) when tests run in CI, when several people test at once, or when a service refuses disposable domains."
          }
        ]
      },
      {
        "id": "workflow",
        "title": "A repeatable test workflow",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Name each address after what it tests, for example signup-1042@otp.example.com, so a failure points straight at its message.",
              "Trigger the flow: sign up, request a reset or send the invitation.",
              "Read the message and check the sender, subject, code and links.",
              "Leave it. Messages expire on their own, so there is nothing to clean up between runs."
            ]
          }
        ]
      },
      {
        "id": "responsible",
        "title": "Using temporary email responsibly",
        "blocks": [
          {
            "type": "paragraph",
            "text": "MailBots is for testing systems you own or are authorized to test, for development and for legitimate automation. It is not for creating accounts in bulk, getting around another service's limits or hiding who you are from someone you owe an answer to. The [acceptable use policy](/acceptable-use) sets out what is allowed."
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "How long does a temporary MailBots address last?"
          },
          {
            "type": "paragraph",
            "text": "Free addresses last 24 hours and can be extended from the extension. Catch-all addresses never expire, because any address on your subdomain works; each message is kept for 72 hours."
          },
          {
            "type": "subheading",
            "text": "Can I reply or send from a temporary address?"
          },
          {
            "type": "paragraph",
            "text": "No. MailBots is receive-only on every plan."
          },
          {
            "type": "subheading",
            "text": "Is temporary email safe to use for testing?"
          },
          {
            "type": "paragraph",
            "text": "For test accounts, yes. Do not use any temporary inbox for accounts that hold money, legal notices or anything you need to recover later."
          }
        ]
      }
    ]
  },
  {
    "path": "/catch-all-email-testing",
    "label": "Catch-all email testing",
    "title": "Catch-all Email Testing on Your Own Subdomain | MailBots",
    "heading": "Give every test its own email address",
    "description": "Point a subdomain such as otp.example.com at MailBots and every address under it receives mail. Read test email on the web, over IMAPS or POP3S, by API or by webhook.",
    "lead": "Catch-all email testing lets every address on a subdomain receive mail, so each test can invent its own address, such as signup-1042@otp.example.com, with nothing to create in advance. MailBots receives mail for a subdomain you point at it with one MX record. Your company email stays untouched, and messages are readable on the web, over IMAPS and POP3S, through a REST API or by signed webhook.",
    "cta": "plans",
    "updated": "2026-09-24",
    "related": [
      "/pricing",
      "/docs/dns",
      "/docs/api",
      "/docs/webhooks"
    ],
    "sections": [
      {
        "id": "how",
        "title": "How it works",
        "blocks": [
          {
            "type": "steps",
            "items": [
              "Choose a subdomain you control, for example `otp.example.com`.",
              "Add a TXT record that proves you own it and an MX record that sends its mail to MailBots.",
              "MailBots checks both records, then asks you to send one email from an outside account to confirm that mail really arrives.",
              "Provision the mailbox and save its credentials, which are shown once. From then on, every address at `otp.example.com` lands in that mailbox."
            ]
          },
          {
            "type": "facts",
            "rows": [
              [
                "TXT name",
                "_mailbots-challenge.otp.example.com"
              ],
              [
                "TXT content",
                "mailbots-verification=mb-…"
              ],
              [
                "MX name",
                "otp.example.com"
              ],
              [
                "MX target",
                "mx1.mailbots.top"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "The dashboard shows the exact values for your domain. The [DNS setup guide](/docs/dns) walks through every step, including DNS providers that display relative names."
          }
        ]
      },
      {
        "id": "subdomain",
        "title": "Why a subdomain and not your main domain",
        "blocks": [
          {
            "type": "paragraph",
            "text": "MX records apply to one name at a time. Pointing `otp.example.com` at MailBots changes where mail for that subdomain goes and nothing else: mail to you@example.com keeps reaching your company's mail provider."
          },
          {
            "type": "note",
            "text": "Never change the MX records of your root domain for testing. MailBots only ever asks for the subdomain."
          }
        ]
      },
      {
        "id": "access",
        "title": "Ways to read the mail",
        "blocks": [
          {
            "type": "table",
            "head": [
              "Method",
              "Best for",
              "Details"
            ],
            "rows": [
              [
                "Web inbox",
                "Manual QA",
                "The MailBots dashboard"
              ],
              [
                "IMAPS",
                "Mail clients and scripts",
                "`mx1.mailbots.top`, port 993"
              ],
              [
                "POP3S",
                "Simple downloads",
                "`mx1.mailbots.top`, port 995"
              ],
              [
                "REST API",
                "Assertions in CI",
                "[Messages with the detected code](/docs/api)"
              ],
              [
                "Webhooks",
                "Event-driven tests",
                "[Signed message.received events](/docs/webhooks)"
              ]
            ]
          },
          {
            "type": "paragraph",
            "text": "The [mail client guide](/docs/mail-clients) covers IMAPS and POP3S settings. For browser tests, see the [Playwright](/docs/playwright-email-testing) and [Cypress](/docs/cypress-email-testing) guides."
          }
        ]
      },
      {
        "id": "isolation",
        "title": "Isolation and security",
        "blocks": [
          {
            "type": "bullets",
            "items": [
              "Each subdomain gets its own mailbox, quota and credentials.",
              "API keys are scoped to `domains:read`, `messages:read` and `usage:read`, shown once and revocable at any time.",
              "Webhook requests are signed with HMAC-SHA256, so your endpoint can reject anything MailBots did not send.",
              "Raw messages are deleted from the mail gateway after 72 hours, and the text and codes kept for the dashboard and API follow the same limit.",
              "MailBots is receive-only. There is no SMTP submission, so a leaked mailbox password cannot send mail as your domain."
            ]
          }
        ]
      },
      {
        "id": "plans",
        "title": "Plans",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Starter costs $10 a month for one subdomain, 5,000 messages a month and 250 MB of storage. Pro costs $15 a month for five subdomains, 25,000 messages a month, 1 GB of storage and three team members. Both include the web inbox, IMAPS, POP3S, the REST API and webhooks. See the [full comparison](/pricing)."
          }
        ]
      },
      {
        "id": "faq",
        "title": "Questions",
        "blocks": [
          {
            "type": "subheading",
            "text": "Will a catch-all subdomain affect our company email?"
          },
          {
            "type": "paragraph",
            "text": "No, as long as you only change records for the subdomain. Your root domain's MX records, and the mail they deliver, stay exactly as they are."
          },
          {
            "type": "subheading",
            "text": "Does it work with any DNS provider?"
          },
          {
            "type": "paragraph",
            "text": "Any provider that lets you add TXT and MX records for a subdomain works, including Cloudflare, Amazon Route 53 and most registrars."
          },
          {
            "type": "subheading",
            "text": "Can one account use several subdomains?"
          },
          {
            "type": "paragraph",
            "text": "Pro allows five, which suits separate environments such as staging, preview and CI. Starter allows one."
          },
          {
            "type": "subheading",
            "text": "What happens to mail after 72 hours?"
          },
          {
            "type": "paragraph",
            "text": "It is deleted automatically, from the mail gateway and from the dashboard and API alike. Export anything you need to keep before then."
          }
        ]
      }
    ]
  }
];
